import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Button, Card, Descriptions, Space, Tag, Typography, Skeleton, Alert } from 'antd';
import { ArrowLeftOutlined, PlayCircleOutlined, ClockCircleOutlined, DollarOutlined } from '@ant-design/icons';
import { getUroSDK } from '../../../services/URO';
import ScrollPanel from '../../../components/ScrollPanel';
import colors from '../../../utils/theme/colors';
import BeforeAfterPreview from '../../../components/BeforeAfterPreview';
import { FallbackIcon } from '../dashboardScreen/components/FallbackIcon';
import CodeExampleCard from '../../../components/apiDocs/CodeExampleCard';
import ResponseExampleCard from '../../../components/apiDocs/ResponseExampleCard';
import ApiEndpointBadge from '../../../components/apiDocs/ApiEndpointBadge';
import {
  generateWorkflowCurl,
  generateWorkflowAxios,
  generateWorkflowPython,
  generateCheckWorkflowJobCurl,
  generateCheckWorkflowJobAxios,
  generateCheckWorkflowJobPython,
} from '../../../utils/api/codeGenerators';
import { getWorkflowRunResponses, getWorkflowJobResponses } from '../../../utils/api/responseExamples';

const { Title, Text, Paragraph } = Typography;

function WorkflowDetailScreen() {
  const { workflowId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [imageError, setImageError] = useState(false);

  // Fetch workflow details
  useEffect(() => {
    const fetchWorkflow = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await getUroSDK().getWorkflowById(workflowId);
        setWorkflow(data);
      } catch (e) {
        setError(e.message || 'Failed to load workflow details');
      } finally {
        setLoading(false);
      }
    };

    if (workflowId) {
      fetchWorkflow();
    }
  }, [workflowId]);

  const handleTryWorkflow = () => {
    navigate(`/zen/workflow/${encodeURIComponent(workflowId)}`);
  };

  if (loading) {
    return (
      <ScrollPanel scrollable={false} style={{ height: 'auto' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px' }}>
          <Skeleton active avatar paragraph={{ rows: 8 }} />
        </div>
      </ScrollPanel>
    );
  }

  if (error) {
    return (
      <ScrollPanel scrollable={false} style={{ height: 'auto' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px' }}>
          <Alert
            message="Error Loading Workflow"
            description={error}
            type="error"
            showIcon
            action={
              <Button size="small" onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </Button>
            }
          />
        </div>
      </ScrollPanel>
    );
  }

  if (!workflow) {
    return (
      <ScrollPanel scrollable={false} style={{ height: 'auto' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px' }}>
          <Alert
            message="Workflow Not Found"
            description="The requested workflow could not be found."
            type="warning"
            showIcon
            action={
              <Button size="small" onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </Button>
            }
          />
        </div>
      </ScrollPanel>
    );
  }

  return (
    <ScrollPanel scrollable={false} style={{ height: 'auto' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px' }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/dashboard')}
            style={{ marginBottom: 16 }}
          >
            Back to Dashboard
          </Button>
          
          <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
            {/* Workflow Image */}
            <div style={{ flexShrink: 0 }}>
              <Card 
                style={{ 
                  width: 300, 
                  background: colors.card,
                  border: `1px solid ${colors.border}`
                }}
                bodyStyle={{ padding: 0 }}
              >
                <div style={{ position: 'relative', overflow: 'hidden' }}>
                  {!imageError && workflow.cover_image_url && workflow.result_example_url ? (
                    <BeforeAfterPreview
                      before={workflow.cover_image_url}
                      after={workflow.result_example_url}
                      randomize={true}
                      style={{ 
                        width: '100%', 
                        aspectRatio: '1/1'
                      }}
                    />
                  ) : !imageError && workflow.cover_image_url ? (
                    <img
                      alt={workflow.name}
                      src={workflow.cover_image_url}
                      onError={() => setImageError(true)}
                      style={{ 
                        width: '100%', 
                        aspectRatio: '1/1', 
                        objectFit: 'cover', 
                        display: 'block'
                      }}
                    />
                  ) : (
                    <FallbackIcon 
                      type="workflow" 
                      size={300} 
                      style={{ 
                        borderRadius: 0, 
                        aspectRatio: '1/1'
                      }} 
                    />
                  )}
                </div>
              </Card>
            </div>

            {/* Workflow Info */}
            <div style={{ flex: 1 }}>
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <div>
                  <Title level={1} style={{ margin: 0, color: colors.foreground }}>
                    {workflow.name}
                  </Title>
                  <Tag 
                    style={{ 
                      marginTop: 8,
                      borderRadius: 12,
                      border: 'none',
                      background: 'rgba(236, 72, 153, 0.1)',
                      color: '#ec4899',
                      fontWeight: 500,
                      fontSize: 12,
                      padding: '4px 12px'
                    }}
                  >
                    Workflow
                  </Tag>
                </div>

                <Paragraph style={{ color: colors.muted_foreground, fontSize: 16 }}>
                  {workflow.description || 'Multi-step AI workflow for complex tasks'}
                </Paragraph>

                {/* Quick Stats */}
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                  {workflow.estimated_cost && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <DollarOutlined style={{ color: '#10b981' }} />
                      <Text style={{ color: colors.foreground }}>
                        <span style={{ fontSize: 12, opacity: 0.8 }}>~</span>
                        {Number(workflow.estimated_cost).toFixed(4)} URO per run
                      </Text>
                    </div>
                  )}
                  
                  {workflow.estimated_time && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <ClockCircleOutlined style={{ color: '#f59e0b' }} />
                      <Text style={{ color: colors.foreground }}>
                        ~{workflow.estimated_time}s runtime
                      </Text>
                    </div>
                  )}

                  {workflow.steps && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Text style={{ color: colors.foreground }}>
                        {workflow.steps.length} steps
                      </Text>
                    </div>
                  )}
                </div>

                {/* CTA Button */}
                <Button
                  type="primary"
                  size="large"
                  icon={<PlayCircleOutlined />}
                  onClick={handleTryWorkflow}
                  style={{
                    background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
                    border: 'none',
                    borderRadius: 8,
                    height: 48,
                    fontSize: 16,
                    fontWeight: 600
                  }}
                >
                  Try This Workflow
                </Button>
              </Space>
            </div>
          </div>
        </div>

        {/* Detailed Information */}
        <Card 
          title="Workflow Details" 
          style={{ 
            background: colors.card,
            border: `1px solid ${colors.border}`
          }}
        >
          <Descriptions column={2} bordered>
            <Descriptions.Item label="Workflow ID">
              <Text code>{workflow.workflow_definition_id}</Text>
            </Descriptions.Item>
            
            <Descriptions.Item label="Type">
              Multi-step Workflow
            </Descriptions.Item>
            
            {workflow.estimated_cost && (
              <Descriptions.Item label="Estimated Cost">
                <Text style={{ color: '#10b981', fontWeight: 600 }}>
                  <span style={{ fontSize: 12, opacity: 0.8 }}>~</span>
                  {Number(workflow.estimated_cost).toFixed(4)} URO per run
                </Text>
              </Descriptions.Item>
            )}
            
            {workflow.estimated_time && (
              <Descriptions.Item label="Estimated Runtime">
                <Text style={{ color: '#f59e0b', fontWeight: 600 }}>
                  ~{workflow.estimated_time} seconds
                </Text>
              </Descriptions.Item>
            )}
            
            {workflow.steps && (
              <Descriptions.Item label="Number of Steps">
                {workflow.steps.length}
              </Descriptions.Item>
            )}
            
            {workflow.created_at && (
              <Descriptions.Item label="Created">
                {new Date(workflow.created_at).toLocaleDateString()}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        {/* Workflow Steps */}
        {workflow.steps && workflow.steps.length > 0 && (
          <Card 
            title="Workflow Steps" 
            style={{ 
              marginTop: 24,
              background: colors.card,
              border: `1px solid ${colors.border}`
            }}
          >
            <div style={{ display: 'grid', gap: 16 }}>
              {workflow.steps.map((step, index) => (
                <div 
                  key={index}
                  style={{
                    padding: 16,
                    background: colors.muted,
                    borderRadius: 8,
                    border: `1px solid ${colors.border}`,
                    position: 'relative'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 600
                      }}
                    >
                      {index + 1}
                    </div>
                    <Text strong style={{ color: colors.foreground }}>
                      {step.name || `Step ${index + 1}`}
                    </Text>
                  </div>
                  
                  {step.description && (
                    <Paragraph style={{ color: colors.muted_foreground, margin: '8px 0 0 36px' }}>
                      {step.description}
                    </Paragraph>
                  )}
                  
                  {step.model && (
                    <Text style={{ color: colors.muted_foreground, fontSize: 12, marginLeft: 36 }}>
                      Model: {step.model}
                    </Text>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Parameters Section */}
        {workflow.parameters && workflow.parameters.length > 0 && (
          <Card
            title="Accepted Parameters"
            style={{
              marginTop: 24,
              background: colors.card,
              border: `1px solid ${colors.border}`
            }}
          >
            <div style={{ display: 'grid', gap: 16 }}>
              {workflow.parameters.map((param, index) => {
                const isRequired = Number(param.is_required) === 1 || param.is_required === true;
                let parsedAllowed = null;
                if (param.allowed_values) {
                  try {
                    parsedAllowed = typeof param.allowed_values === 'string'
                      ? JSON.parse(param.allowed_values)
                      : param.allowed_values;
                  } catch { parsedAllowed = null; }
                }
                return (
                  <div
                    key={index}
                    style={{
                      padding: 16,
                      background: colors.muted,
                      borderRadius: 8,
                      border: `1px solid ${colors.border}`
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <div>
                        <Text strong style={{ color: colors.foreground, fontSize: 15 }}>
                          {param.parameter_title}
                        </Text>
                        {param.parameter_name && (
                          <Text code style={{ marginLeft: 8, fontSize: 12, color: colors.muted_foreground }}>
                            {param.parameter_name}
                          </Text>
                        )}
                      </div>
                      <Tag color={isRequired ? 'red' : 'blue'}>
                        {isRequired ? 'Required' : 'Optional'}
                      </Tag>
                    </div>

                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: param.description ? 8 : 0 }}>
                      <Text style={{ color: colors.muted_foreground, fontSize: 12 }}>
                        Type: <Text code style={{ fontSize: 12 }}>{param.data_type}</Text>
                      </Text>
                      {param.default_value != null && param.default_value !== '' && (
                        <Text style={{ color: colors.muted_foreground, fontSize: 12 }}>
                          Default: <Text code style={{ fontSize: 12 }}>{String(param.default_value)}</Text>
                        </Text>
                      )}
                      {(param.allowed_min_value != null || param.allowed_max_value != null) && (
                        <Text style={{ color: colors.muted_foreground, fontSize: 12 }}>
                          Range: <Text code style={{ fontSize: 12 }}>
                            {param.allowed_min_value != null ? param.allowed_min_value : '∞'}
                            {' – '}
                            {param.allowed_max_value != null ? param.allowed_max_value : '∞'}
                          </Text>
                        </Text>
                      )}
                    </div>

                    {param.description && (
                      <Paragraph style={{ color: colors.muted_foreground, margin: '4px 0 0 0' }}>
                        {param.description}
                      </Paragraph>
                    )}

                    {parsedAllowed && Array.isArray(parsedAllowed) && parsedAllowed.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <Text style={{ color: colors.muted_foreground, fontSize: 12, display: 'block', marginBottom: 4 }}>
                          Allowed values:
                        </Text>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {parsedAllowed.map((val, i) => (
                            <Tag
                              key={i}
                              style={{
                                background: 'rgba(250, 139, 20, 0.1)',
                                border: '1px solid rgba(250, 139, 20, 0.3)',
                                color: '#FA8B14',
                                fontSize: 12,
                                borderRadius: 6,
                              }}
                            >
                              {String(val)}
                            </Tag>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* API Integration Section */}
        <div style={{ marginTop: 40 }}>
          <Title level={2} style={{ color: colors.foreground, marginBottom: 8 }}>
            API Integration
          </Title>
          <Paragraph style={{ color: colors.muted_foreground, marginBottom: 24 }}>
            Use the URO API to run this workflow programmatically. The code examples below are pre-filled with this workflow's ID and parameters.
          </Paragraph>

          <ApiEndpointBadge method="POST" path="/api/workflows/run-workflow" />

          <CodeExampleCard
            title="Step 1: Run This Workflow"
            description={`Send a POST request with workflow_definition_id "${workflow.workflow_definition_id}" and the input parameters inside the input_params object.`}
            codeExamples={{
              curl: generateWorkflowCurl(workflow),
              axios: generateWorkflowAxios(workflow),
              python: generateWorkflowPython(workflow),
            }}
          />

          <ResponseExampleCard
            title="Run Workflow - Response Examples"
            responses={getWorkflowRunResponses(workflow)}
          />

          <div style={{ marginTop: 32 }}>
            <ApiEndpointBadge method="GET" path="/api/workflows/workflow-jobs/workflow-job/{job_id}" />
          </div>

          <CodeExampleCard
            title="Step 2: Check Workflow Job Status"
            description="After submitting a workflow job, poll this endpoint with the workflow_job_id from the response above. Workflow jobs may take longer since they involve multiple steps."
            codeExamples={{
              curl: generateCheckWorkflowJobCurl(),
              axios: generateCheckWorkflowJobAxios(),
              python: generateCheckWorkflowJobPython(),
            }}
          />

          <ResponseExampleCard
            title="Workflow Job Status - Response Example"
            responses={getWorkflowJobResponses(workflow)}
          />
        </div>
      </div>
    </ScrollPanel>
  );
}

export default WorkflowDetailScreen;
